/**
 * The warning for a chosen style the server would ignore.
 *
 * Each reason is one whole sentence rather than a phrase assembled from fragments: the operator
 * has to be able to match this word for word against the amber banner a save returns, and a
 * translator has to be able to reorder it. The English defaults are the server's own wording, from
 * `ignoredStyleWarning()` in
 * `backend/core/src/main/java/com/cubeia/nano/services/json/JsonDocumentValidationService.java`.
 */
import { t } from "../../../i18n";
import { EnforcedStyleUnusable } from "./enforcedStyleVerdict";

interface Props {
  /** The verdict to explain, or `undefined` when the chosen style is fine as it stands. */
  verdict: EnforcedStyleUnusable | undefined;
}

function unusableMessage(verdict: EnforcedStyleUnusable): string {
  const style = verdict.style;

  if (verdict.reason === "non-positive-size") {
    const { width, height } = verdict;
    return t(
      "enforced-thumbnail-style.unusable-non-positive-size",
      { style, width, height },
      `default-style "${style}" has a style-sizes entry of ${width}x${height}, which is not a positive width and height, so it will be ignored and thumbnails render as if no default style were set. Set the style-sizes entry for "${style}" to the pixel width and height of that style's source images.`,
    );
  }

  if (verdict.reason === "no-url-template") {
    return t(
      "enforced-thumbnail-style.unusable-no-url-template",
      { style },
      `default-style "${style}" has no usable url template under styles, so it will be ignored and thumbnails render as if no default style were set. Give "${style}" a non-blank styles entry holding the image url template for that style.`,
    );
  }

  return t(
    "enforced-thumbnail-style.unusable-no-size-entry",
    { style },
    `default-style "${style}" has no style-sizes entry, so it will be ignored and thumbnails render as if no default style were set. Add a style-sizes entry for "${style}" with a positive width and height.`,
  );
}

export function VerdictNotice({ verdict }: Props): JSX.Element | null {
  if (verdict === undefined) {
    return null;
  }

  return (
    <div
      className="alert alert-warning"
      role="alert"
      data-testid="enforced-style-verdict"
    >
      {unusableMessage(verdict)}
    </div>
  );
}
